import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header, Footer } from "../components/Layout";
import SEO from "../components/SEO";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sun, Moon, Heart, Briefcase, Coin, Pulse, Sparkle, ArrowLeft, Warning, MapPin, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import ToolAstrologerCta from "../components/ToolAstrologerCta";
import PremiumTeaser from "../components/PremiumTeaser";
import LocationPicker, { getStoredTz, setStoredTz, prettyCity, offsetFor, localDateLabel, detectBrowserTz } from "../components/LocationPicker";

/**
 * HoroscopeLoader — percentage-based progress UI shown while the
 * personalised horoscope is generating on the backend (typical Haiku
 * 4.5 wall-time ≈ 1.5-3 s from a cold cache).
 *
 * Design: ticks 0 → 95 over ~3.5 s along a natural easing curve
 * (fast start, slow finish) so it feels like real work is happening.
 * Stays at 95% if the request takes longer than expected — the
 * moment the parent's `busy` flips false and `horo` renders, this
 * component unmounts, which visually looks like the bar snapping to
 * 100%. The rotating message strip keeps the perceived wait time
 * shorter than the actual wait.
 */
function HoroscopeLoader() {
  const [pct, setPct] = useState(0);
  const [msgIdx, setMsgIdx] = useState(0);

  const messages = React.useMemo(
    () => [
      "Reading your natal chart…",
      "Checking today's Moon transit…",
      "Aligning your dasha with the day…",
      "Blessing the details with care…",
      "Almost there — finalising your day…",
    ],
    [],
  );

  useEffect(() => {
    // Progress curve: sharp early rise then a long tail — matches how
    // real network requests feel. Interval fires every 100 ms; step
    // size shrinks as we approach 95%.
    const iv = setInterval(() => {
      setPct((p) => {
        if (p >= 95) return 95;
        // Diminishing step: bigger jumps early, smaller near the top.
        const step = Math.max(0.4, (95 - p) / 22);
        return Math.min(95, p + step);
      });
    }, 100);
    // Rotate the message every 1.4 s
    const mv = setInterval(() => {
      setMsgIdx((i) => (i + 1) % 5);
    }, 1400);
    return () => {
      clearInterval(iv);
      clearInterval(mv);
    };
  }, []);

  return (
    <div
      className="mt-8 rounded-2xl p-6 shadow-lg"
      data-testid="horoscope-loading"
      style={{
        background: "linear-gradient(135deg, #3F2D1B 0%, #1A1410 100%)",
        color: "#FAF4E8",
      }}
    >
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{
            background: "radial-gradient(circle at 50% 40%, #FFE08A 0%, #FFB800 60%, #E89200 100%)",
            boxShadow: "0 0 24px rgba(255,184,0,0.55)",
          }}
        >
          <Sparkle size={22} weight="fill" color="#3F2D1B" />
        </div>
        <div className="flex-1">
          <div className="text-amber-100 text-sm font-semibold">
            Reading the stars for you
          </div>
          <div className="text-amber-100/70 text-xs mt-0.5 transition-opacity" key={msgIdx}>
            {messages[msgIdx]}
          </div>
        </div>
        <div
          className="text-amber-300 font-bold text-lg tabular-nums"
          data-testid="horoscope-loading-pct"
        >
          {Math.round(pct)}%
        </div>
      </div>
      {/* Progress track */}
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ background: "rgba(255,224,138,0.15)" }}
      >
        <div
          className="h-full rounded-full transition-all duration-100 ease-out"
          style={{
            width: `${pct}%`,
            background:
              "linear-gradient(90deg, #FFD66B 0%, #FFB800 50%, #FFE08A 100%)",
            boxShadow: "0 0 12px rgba(255,184,0,0.6)",
          }}
        />
      </div>
    </div>
  );
}


export default function DailyHoroscopePage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [horo, setHoro] = useState(null);
  const [busy, setBusy] = useState(false);
  const [currentTz, setCurrentTz] = useState(() => getStoredTz());
  const [showPicker, setShowPicker] = useState(false);

  // First visit gates on the location picker. Returning users skip the
  // prompt unless they hit the "change location" pencil icon.
  useEffect(() => {
    if (loading) return;
    if (!user) {
      toast("Sign in to unlock your personalised daily horoscope.");
      navigate("/");
      return;
    }
    if (!currentTz) {
      setShowPicker(true);
    }
  }, [user, loading, navigate, currentTz]);

  // Fetch (or refetch) the horoscope whenever the resolved tz changes.
  useEffect(() => {
    if (loading || !user || !currentTz || showPicker) return;
    let cancelled = false;
    (async () => {
      try {
        setBusy(true);
        const { data } = await api.get("/horoscope/daily", {
          params: { current_tz: currentTz },
        });
        if (!cancelled) setHoro(data);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 400 && detail?.toLowerCase().includes("birth details")) {
          toast.error(detail);
          navigate("/dashboard");
          return;
        }
        toast.error(detail || "Couldn't load today's horoscope. Try again in a moment.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, loading, navigate, currentTz, showPicker]);

  const handleLocationConfirm = (tz) => {
    setStoredTz(tz);
    setCurrentTz(tz);
    setShowPicker(false);
    // Force a refetch: clear stale horoscope so the loader shows
    setHoro(null);
  };

  const todayLabel = currentTz
    ? localDateLabel(currentTz)
    : new Date().toLocaleDateString("en-IN", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });

  return (
    <div className="bg-[#FAF4E8] min-h-[100dvh] flex flex-col" data-testid="horoscope-page">
      <SEO
        title="Daily Horoscope Today — All 12 Rashi Predictions (Free)"
        description="Free daily horoscope for all 12 Rashis — Aries, Taurus, Gemini, Cancer, Leo, Virgo, Libra, Scorpio, Sagittarius, Capricorn, Aquarius, Pisces. Career, love, health and finance reading for today computed from current planetary transits."
        path="/horoscope"
        keywords={[
          "daily horoscope", "horoscope today", "today's horoscope",
          "free horoscope", "rashi horoscope", "rashifal today",
          "aries horoscope", "taurus horoscope", "gemini horoscope",
          "cancer horoscope", "leo horoscope", "virgo horoscope",
          "libra horoscope", "scorpio horoscope", "sagittarius horoscope",
          "capricorn horoscope", "aquarius horoscope", "pisces horoscope",
          "love horoscope today", "career horoscope today",
        ]}
      />
      <Header />
      <main className="flex-1 max-w-4xl mx-auto w-full px-4 sm:px-6 lg:px-10 py-6 sm:py-10">
        <button
          onClick={() => navigate(-1)}
          className="md:hidden flex items-center gap-1 text-sm text-muted mb-4"
          data-testid="horoscope-back-btn"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="label-eyebrow !text-[10px] sm:!text-xs">{todayLabel}</div>
        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-tight text-[#3F2D1B] mt-1">
          Your daily horoscope
        </h1>
        <p className="text-muted text-base sm:text-lg mt-3 max-w-2xl">
          {user?.name ? `Dear ${user.name.split(" ")[0]}, ` : ""}
          here is what today holds for your <b className="text-[#3F2D1B]">{horo?.lagna || "—"} lagna</b> and{" "}
          <b className="text-[#3F2D1B]">{horo?.moon_rashi || "—"} moon</b>
          {horo?.moon_nakshatra && (
            <>
              {" "}({horo.moon_nakshatra} nakshatra)
            </>
          )}
          .
        </p>

        {/* Current-location pill (visible once a tz is picked). Tapping the pencil re-opens the picker. */}
        {currentTz && !showPicker && (
          <button
            type="button"
            onClick={() => setShowPicker(true)}
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#FFF6E0]/70 border border-amber-200 px-3.5 py-1.5 text-xs sm:text-sm text-[#3F2D1B] hover:bg-amber-100/80 transition group"
            data-testid="horoscope-location-pill"
          >
            <MapPin size={14} weight="duotone" className="text-amber-700" />
            <span className="font-medium">Reading from</span>
            <span className="text-[#3F2D1B]/80">{prettyCity(currentTz)}</span>
            <span className="text-[#3F2D1B]/50">· {offsetFor(currentTz)}</span>
            <PencilSimple size={12} className="text-amber-700 ml-1 opacity-60 group-hover:opacity-100" />
          </button>
        )}

        {showPicker && (
          <div className="mt-8" data-testid="horoscope-location-gate">
            <LocationPicker
              initialTz={currentTz || detectBrowserTz()}
              onConfirm={handleLocationConfirm}
              onCancel={currentTz ? () => setShowPicker(false) : undefined}
            />
          </div>
        )}

        {busy && !horo && !showPicker && (
          <HoroscopeLoader />
        )}

        {horo && !showPicker && (
          <>
            {/* Headline card */}
            <div className="mt-8 rounded-2xl bg-gradient-to-br from-[#3F2D1B] to-[#1A1410] text-[#FAF4E8] p-6 sm:p-8 shadow-xl border border-amber-700/40" data-testid="horoscope-headline">
              <div className="flex items-center gap-2 text-amber-300 text-[11px] uppercase tracking-widest mb-3">
                <Sun size={14} weight="fill" /> Today&apos;s vibe
              </div>
              <h2 className="font-serif-display text-2xl sm:text-3xl leading-tight">{horo.headline}</h2>
              <p className="mt-4 text-amber-50/90 leading-relaxed text-sm sm:text-base">{horo.overall}</p>

              {/* Quick stats — stacked rows on mobile (label left, value right) so long values like 
                  "Sunset Saffron" or "6:00 PM – 9:00 PM" never get clipped. Desktop: 3-col grid. */}
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2.5 sm:gap-3">
                <Stat label="Lucky color" value={horo.lucky_color} />
                <Stat label="Lucky number" value={horo.lucky_number} />
                <Stat label="Best time" value={horo.best_time} />
              </div>
            </div>

            {/* Moon transit + Chandrashtama strip */}
            {horo.moon_transit && (
              <div
                className={`mt-6 rounded-2xl p-5 sm:p-6 border ${
                  horo.is_chandrashtama
                    ? "bg-rose-50 border-rose-300 text-rose-900"
                    : "bg-indigo-50 border-indigo-200 text-indigo-950"
                }`}
                data-testid="horoscope-moon-transit"
              >
                <div className="flex items-start gap-3">
                  {horo.is_chandrashtama ? (
                    <Warning size={26} weight="duotone" className="text-rose-600 flex-shrink-0 mt-0.5" />
                  ) : (
                    <Moon size={26} weight="duotone" className="text-indigo-600 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="label-eyebrow !text-[10px] flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span>Moon today</span>
                      <span className={horo.is_chandrashtama ? "text-rose-700" : "text-indigo-700"}>
                        · {horo.moon_transit.transit_rashi}
                      </span>
                      <span className="text-[#3F2D1B]/60 normal-case tracking-normal">
                        ({horo.moon_transit.transit_nakshatra})
                      </span>
                      <span className="px-2 py-0.5 rounded-full bg-white/60 border border-current/20 text-[10px] tracking-widest font-semibold">
                        {horo.moon_transit.house_from_natal_moon}<sup>th</sup> from your Moon
                      </span>
                    </div>
                    {horo.is_chandrashtama ? (
                      <div className="mt-2">
                        <div className="font-serif-display text-lg sm:text-xl leading-tight">
                          ⚠ Chandrashtama day, dear
                        </div>
                        <p className="text-sm sm:text-[15px] mt-1.5 leading-snug">
                          The Moon is in your 8<sup>th</sup> rashi today. Best to avoid signing
                          contracts, starting new ventures, or major decisions.
                          Rest, eat warm food, and do a small evening prayer instead — tomorrow is yours.
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm sm:text-[15px] mt-2 leading-snug">
                        {horo.moon_transit.note || `The transiting Moon is gracing the ${horo.moon_transit.house_from_natal_moon}th house from your natal Moon today.`}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 4-quadrant life areas */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="horoscope-quadrants">
              <AreaCard icon={Briefcase} label="Career" tone="amber" text={horo.career} />
              <AreaCard icon={Heart} label="Love" tone="rose" text={horo.love} />
              <AreaCard icon={Coin} label="Money" tone="emerald" text={horo.money} />
              <AreaCard icon={Pulse} label="Health" tone="sky" text={horo.health} />
            </div>

            {/* Remedy & mantra */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="card-surface rounded-2xl p-5 sm:p-6 bg-[#FFF6E0]/60 border border-amber-200" data-testid="horoscope-remedy">
                <div className="label-eyebrow !text-[10px] flex items-center gap-1.5">
                  <Sparkle size={12} weight="fill" className="text-gold" /> Today&apos;s remedy
                </div>
                <p className="text-[#3F2D1B] text-sm sm:text-base mt-2 leading-snug">{horo.remedy}</p>
              </div>
              <div className="card-surface rounded-2xl p-5 sm:p-6 bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200" data-testid="horoscope-mantra">
                <div className="label-eyebrow !text-[10px] flex items-center gap-1.5">
                  <Moon size={12} weight="fill" className="text-gold" /> Mantra for today
                </div>
                <p className="font-serif-display text-lg sm:text-xl text-[#3F2D1B] mt-2 leading-snug">{horo.mantra}</p>
              </div>
            </div>

            {/* Funnel teaser — daily horoscope users → 20-Year Report */}
            <div className="mt-8" data-testid="horoscope-premium-teaser-wrap">
              <PremiumTeaser
                testid="horoscope-premium-teaser"
                topLine={<>
                  Today&apos;s weather is one page.<br/>
                  <span className="text-amber-300">Your next 20 years is 55+.</span>
                </>}
                subLine={<>
                  Your <b className="text-amber-100">{horo?.moon_rashi || "Moon rashi"}</b>
                  {horo?.moon_nakshatra ? <> + <b className="text-amber-100">{horo.moon_nakshatra}</b> nakshatra</> : null} shape a much
                  bigger story than today&apos;s brief. Unlock your Sade Sati window, 6 doshas, gem &amp; rudraksha,
                  personalised dasha-bhukti timeline and year-by-year career / marriage / wealth / health scores.
                </>}
              />
            </div>

            {/* CTA — Prashna (Horary) for live daily decisions */}
            <div className="mt-8" data-testid="horoscope-cta">
              <ToolAstrologerCta
                requiredTechnique="Prashna (Horary)"
                pitch={`Get a live hour-by-hour read on today's planetary weather & urgent yes/no decisions`}
                ctaLabel="Talk to"
              />
            </div>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="flex items-center justify-between sm:block gap-3 py-2 sm:py-0 border-t sm:border-t-0 border-amber-700/30 first:border-t-0 sm:text-left">
      <div className="text-[10px] uppercase tracking-widest text-amber-300/80 flex-shrink-0">{label}</div>
      <div className="font-serif-display text-base sm:text-xl text-amber-50 sm:mt-0.5 leading-tight text-right sm:text-left break-words min-w-0">
        {value || "—"}
      </div>
    </div>
  );
}

function AreaCard({ icon: Icon, label, tone, text }) {
  const tones = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
  };
  return (
    <div className={`card-surface rounded-2xl p-5 sm:p-6 ${tones[tone]} border`} data-testid={`horoscope-area-${label.toLowerCase()}`}>
      <div className="flex items-center gap-2 label-eyebrow !text-[10px]">
        <Icon size={14} weight="duotone" /> {label}
      </div>
      <p className="text-sm sm:text-base mt-2 leading-snug">{text}</p>
    </div>
  );
}
